// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import XngZone from "@conklin/App/Components/Common/XngZone";
import { generatePaddingStyles, Padding } from "@conklin/App/EditorTemplates/PaddingField";
import { xIsAuthenticated } from "@conklin/App/Store/Selectors/GeneralSelectors";
import { CustomFieldDefinition } from "@conklin/CustomFieldDefinition";
import { constants, DivWrapper } from "@conklin/XDL/constants";
import { color, layoutContainer, xScreen } from "@conklin/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { css } from "styled-components";

const StyledWrapperX = getStyledWrapper("div") as any; // typeof StyledWrapper & { sticky: boolean };
const enum fields {
    rowCount = "rowCount",
    gap = "gap",
    isFullWidth = "isFullWidth",
    customCSS = "customCSS",
}

interface Props extends WidgetProps {
    fields: {
        [fields.rowCount]: number;
        [fields.gap]: number;
        [fields.isFullWidth]: boolean;
        [fields.customCSS]: string;
        // XNG-Changes
        margin: string;
        overrideStyle: boolean;
        itemsList: {
            fields: {
                name: string;
                fullWidth: boolean;
                boxed: boolean;
                removeFooterOffset: boolean;
                addSeparate: boolean;
                backgroundColor: string;
                contentOutside: boolean;
                contentInnerBorder: boolean;
                showForGuestOnly: boolean;
                padding: Padding;
            };
        }[];
    };
}

const styles: { container?: InjectableCss; itemContainer?: InjectableCss } = {
    container: {
        css: css`
            max-width: 100%;
            ${({ margin }: any) => margin && `margin: ${margin};`}
            > div {
                margin-bottom: ${({ gap }: any) => gap}px;
                &:last-child {
                    margin-bottom: 0;
                }
            }
        `,
    },
    itemContainer: {
        css: css`
            width: 100%;
            ${layoutContainer}

            ${({
                removeFooterOffset,
                addSeparate,
                backgroundColor,
                boxed,
                contentOutside,
                contentInnerBorder,
                padding,
                gap,
            }: any) => css`
                ${removeFooterOffset &&
                css`
                    &&& {
                        margin-bottom: -${constants.footer.offsetTop}px;
                    }
                `}
                ${addSeparate &&
                css`
                    &:has(div:nth-of-type(1):not(:empty)) {
                        border-top: 1px solid ${color("common.border")};
                        padding-top: ${gap || 20}px;
                    }

                    & > ${DivWrapper} > div:empty {
                        display: none;
                    }
                `}
                ${backgroundColor &&
                css`
                    background-color: ${backgroundColor};
                `}

                ${boxed &&
                css`
                    > div {
                        ${layoutContainer};
                    }
                `}
                
                ${contentOutside &&
                css`
                    box-shadow: inset 0 77px 0 0 ${color("common.ui8")};

                    ${xScreen("lte", "sm")} {
                        box-shadow: inset 0 102px 0 0 ${color("common.ui8")};
                    }
                `}
                
                ${contentInnerBorder &&
                css`
                    box-shadow: inset 0 7px 0 0 ${backgroundColor || color("common.ui1")},
                        inset 0 8px 0 0 ${color("common.border")},
                        inset 0 -7px 0 0 ${backgroundColor || color("common.ui1")},
                        inset 0 -8px 0 0 ${color("common.border")};
                `}

                ${padding &&
                css`
                    &&& {
                        > div {
                            ${generatePaddingStyles(padding)}
                        }
                    }
                `}
            `}
        `,
    },
};

// A widget that makes a configurable number of row zones within its container.
const Rows: React.FunctionComponent<Props> = ({
    fields: {
        rowCount,
        gap,
        isFullWidth,
        customCSS,
        // XNG-Changes
        margin,
        overrideStyle,
        itemsList,
    },
    id,
}: Props) => {
    const rows: JSX.Element[] = [];

    // <XNG-Changes>
    const shellContext = useShellContext();
    const isAuthenticated = useAppSelector(state => xIsAuthenticated(state));

    if (overrideStyle) {
        itemsList?.forEach((item, index) => {
            const {
                fullWidth: isFullWidthItem,
                boxed,
                removeFooterOffset,
                addSeparate,
                name: itemName = "",
                backgroundColor,
                contentOutside,
                contentInnerBorder,
                padding,
                showForGuestOnly,
            } = item.fields;

            if (showForGuestOnly && isAuthenticated && !shellContext.isEditing) {
                return;
            }

            const name = itemName.replace(/\s/g, "");
            // "full-width" class for "LayoutContainer" style
            const className = `${isFullWidthItem ? "full-width" : ""}`;

            const itemProps = {
                addSeparate,
                removeFooterOffset: removeFooterOffset && index === itemsList.length - 1,
                backgroundColor,
                isFullWidthItem,
                boxed,
                contentOutside,
                contentInnerBorder,
                padding,
                gap,
            };

            rows.push(
                // eslint-disable-next-line react/no-array-index-key
                <StyledWrapper
                    key={`StyledWrapper-${id}-${name || index}`}
                    className={className}
                    {...itemProps}
                    {...styles.itemContainer}
                >
                    <XngZone contentId={id} zoneName={`Content_${name || index}`} hint={`Zone for ${itemName}`} />
                </StyledWrapper>,
            );
        });
        // </XNG-Changes>
    } else {
        for (let row = 0; row < rowCount; row = row + 1) {
            rows.push(
                <GridItem key={`GridItem-${id}-${row}`} width={[12, 12, 12, 12, 12]}>
                    <Zone contentId={id} zoneName={`Content${row}`} />
                </GridItem>,
            );
        }
    }

    // <XNG-Changes>
    if (overrideStyle) {
        return (
            <StyledWrapperX gap={gap} margin={margin} {...styles.container}>
                {rows}
            </StyledWrapperX>
        );
    }
    // </XNG-Changes>

    return (
        <GridContainer
            css={css`
                ${margin &&
                css`
                    margin: ${margin};
                `}
            `}
            gap={gap}
            isFullWidth={isFullWidth}
        >
            {rows}
        </GridContainer>
    );
};

const defaultCustomCss = `& {

}`;

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule<CustomFieldDefinition> = {
    component: Rows,
    definition: {
        group: "Basic",
        icon: "distribute-spacing-vertical",
        fieldDefinitions: [
            {
                name: fields.rowCount,
                displayName: "Row Count",
                editorTemplate: "IntegerField",
                defaultValue: 4,
                fieldType: "General",
                sortOrder: 1,
                // XNG-Changes
                isVisible: item => !item.fields.overrideStyle,
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.isFullWidth,
                displayName: "Full Width",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: (_item, _advancedFeaturesEnabled, theme) => !!theme?.content?.isFullWidth,
                sortOrder: 3,
            },
            {
                name: "customCSS",
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            // XNG-Changes
            {
                name: "margin",
                displayName: "Margin Size",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "0",
                placeholder: "Enter: 5px, 5px auto, ...etc",
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("margin", value) ? null : `Invalid Input`;
                },
            },
            {
                name: "overrideStyle",
                displayName: "Override Style",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: "itemsList",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.name || "Zone";
                },
                defaultValue: [],
                fieldType: "General",
                isVisible: item => item.fields.overrideStyle,
                fieldDefinitions: [
                    {
                        name: "name",
                        displayName: "Zone Name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        placeholder: "Zone Name",
                        isRequired: true,
                    },
                    {
                        name: "fullWidth",
                        displayName: "Full Width",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "boxed",
                        displayName: "Boxed Layout",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "backgroundColor",
                        displayName: "Background Color",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "#ffffff",
                    },
                    {
                        name: "addSeparate",
                        displayName: "Add Separate",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "removeFooterOffset",
                        displayName: "Remove Footer Offset",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        tooltip: "Use it to remove footer offset, It'll apply for the last item only",
                    },
                    {
                        name: "contentOutside",
                        displayName: "Postion Content Outside",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        tooltip: "Position the content outside the row",
                    },
                    {
                        name: "contentInnerBorder",
                        displayName: "Content Inner Border",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        tooltip: "Show inner Top-Bottom borders",
                    },
                    {
                        name: "showForGuestOnly",
                        displayName: "Show For Guest Users Only",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "padding",
                        displayName: "Padding",
                        editorTemplate: "PaddingField" as any,
                        defaultValue: {} as any,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
